import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { HeroReveal, Pressable, TextReveal } from '@/components/motion'
import { BadgeMarquee } from '@/components/landing/listing'
import { Highlight, type HighlightColor } from '@/components/landing/about/Highlight'
import type { IndustryData } from '@/data/industries'
import { ClinicalFormMedia } from './ClinicalFormMedia'
import { IndustryCtaBand } from './IndustryCtaBand'
import { IndustryListing, sentences } from './listing'
import { RoleGrid } from './RoleGrid'
import { WorkflowJourney } from './WorkflowJourney'

/**
 * The headline as a statement: the last word of the first sentence in a
 * pink box, the last word of the last sentence in a blue one.
 */
function Statement({ text }: { text: string }) {
  const parts = sentences(text)
  const boxed: Record<number, HighlightColor> = { 0: 'pink', [parts.length - 1]: 'blue' }
  return (
    <>
      {parts.map((sentence, i) => {
        const color = boxed[i]
        const cut = sentence.lastIndexOf(' ')
        const space = i < parts.length - 1 ? ' ' : ''
        if (!color || cut === -1) return <Fragment key={i}>{sentence + space}</Fragment>
        return (
          <Fragment key={i}>
            {sentence.slice(0, cut + 1)}
            <Highlight color={color}>{sentence.slice(cut + 1)}</Highlight>
            {space}
          </Fragment>
        )
      })}
    </>
  )
}

/**
 * Statement layout (Healthcare; about reference): centred statement hero
 * with inline highlight boxes, quote + button row, a 16:9 animated clinical
 * form, the roles in the sticky-left grid, the workflows as the journey
 * timeline, the filterable listing, the compliance marquee and a purple
 * CTA band.
 */
export function StatementLayout({ industry }: { industry: IndustryData }) {
  return (
    <>
      {/* 1. Hero: centred 60rem statement, quote + button row */}
      <section aria-labelledby="industry-title" className="pb-[clamp(3rem,8vh,6rem)] pt-[clamp(4rem,12vh,8rem)]">
        <div className="landing-container">
          <div className="mx-auto max-w-[60rem]">
            <HeroReveal delay={0.05}>
              <h1 id="industry-title" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
                <Statement text={industry.headline} />
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-center lg:justify-between lg:gap-24">
                <p className="max-w-[56ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:border-[var(--color-landing-gray-700)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                  {industry.subheadline}
                </p>
                <Pressable className="shrink-0 self-start lg:self-auto">
                  <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                    Book a demo
                    <ArrowRight aria-hidden className="h-5 w-5" />
                  </Link>
                </Pressable>
              </div>
            </HeroReveal>
          </div>
        </div>
      </section>

      {/* 2. Media: the discharge workflow running on a clinical document */}
      <ClinicalFormMedia industry={industry} />

      {/* 3. Roles: sticky left, monogram tiles right */}
      <RoleGrid industry={industry} />

      {/* 4. Workflows as the journey timeline */}
      <WorkflowJourney industry={industry} />

      {/* 5. Listing with filters */}
      <IndustryListing industry={industry} className="border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]" />

      {/* 6. Compliance requirements */}
      {industry.complianceRequirements.length > 0 && (
        <section className="landing-section-sm border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
          <div className="landing-container mb-10">
            <TextReveal as="h2" className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">
              The rules {industry.shortName.toLowerCase()} documents answer to
            </TextReveal>
          </div>
          <BadgeMarquee items={industry.complianceRequirements} label={`${industry.name} compliance requirements`} />
        </section>
      )}

      {/* 7. CTA band: purple */}
      <IndustryCtaBand industry={industry} tone="purple" />
    </>
  )
}
